import { useQuery } from '@apollo/client'
import { Hidden } from '@material-ui/core'
import { useEffect, useRef, useState } from 'react'
import React from 'react'

import { isEmbedded } from '../../../embed/embedMode'
import { GET_WBS_DATA } from '../../../graphql/WBS'
import { DateObject } from '../../../helpers/date'
import { eventOriginatedInside } from '../../../helpers/domEvents'
import { contactPageURL, SITE_KEY } from '../../../helpers/elements'
import { lang } from '../../../localization'
import { WBSData } from '../../../types/WBS'
import { DestinationOption } from '../../../types/accommodationsSubscribedToChannel'
import { SearchAvailabilityInput } from '../../../types/searchAvailability'
import SearchWidgetDesktopInterface from './SearchWidgetDesktop/SearchWidgetDesktopInterface'
import SearchWidgetMobileInterface from './SearchWidgetMobile/SearchWidgetMobileInterface'
import SearchWidgetTabletInterface from './SearchWidgetTablet/SearchWidgetTabletInterface'

interface SearchWidgetMainProps {
  setSearchAvailabilityInput: React.Dispatch<
    React.SetStateAction<SearchAvailabilityInput>
  >
  searchAvailabilityInput: SearchAvailabilityInput
  readMode?: boolean
  setreadMode: React.Dispatch<React.SetStateAction<boolean>>
  isFromBooking?: boolean
}

const SearchWidgetMain: React.FC<SearchWidgetMainProps> = ({
  setSearchAvailabilityInput,
  searchAvailabilityInput,
  readMode,
  setreadMode,
  isFromBooking
}) => {
  const [selectRangeModalIsOpen, setSelectRangeModalIsOpen] =
    useState<boolean>(false)
  const [dateObject, setdateObject] = useState<DateObject>({
    gregorianStartDate: searchAvailabilityInput.arrival,
    gregorianEndDate: searchAvailabilityInput.departure
  })

  const [selectingStartDate, setSelectingStartDate] = useState(true)
  const [guestsNumber, setguestsNumber] = useState<number>(
    searchAvailabilityInput.guestsAges.length
  )
  const [selectNumberOfGuestsModalIsOpen, setSelectNumberOfGuestsModalIsOpen] =
    useState<boolean>(false)
  const [options, setOptions] = useState<DestinationOption[]>([])
  const [bookingRedirect, setBookingRedirect] = useState('')
  const datePickerRef = useRef<HTMLDivElement | null>(null)
  const guestInfoRef = useRef<HTMLDivElement | null>(null)

  const { data: WBSResponse, loading: loadingForWBSData } = useQuery<{
    WBSData: WBSData
  }>(GET_WBS_DATA, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    onCompleted: (res) => {
      const currentUrl = window.location.href
      if (!localStorage.getItem('Referer')) {
        localStorage.setItem('Referer', currentUrl)
      }
      localStorage.setItem('WalletType', res.WBSData.walletType)
    },
    onError: () => {
      // toast.error(e.message)
      //
      // Never navigate in the embed: `window` is the HOTEL'S page there, so redirecting on a
      // transient BFF error would take the hotel's own visitors off the hotel's own site. On the
      // engine domain (standalone app, booking results page) the behaviour is unchanged.
      // See ADR-034 H4.
      if (contactPageURL && !isEmbedded()) {
        window.location.href = contactPageURL
      }
    }
  })

  useEffect(() => {
    // WBSData.showProviderName decides which of the pair is meaningful -- read it
    // explicitly rather than trusting nullish-coalescing precedence on the per-hotel
    // fields. A ?? chain would silently keep working today (the backend's contract is
    // "exactly one is non-null"), but would just as silently disclose the true provider
    // code if that contract were ever violated upstream -- the same failure class this
    // whole initiative exists to close. search-availability accepts either a true
    // provider code or a rate-group handle in the same providerCodes input and
    // disambiguates itself per channel, so this repo only needs to pick the right SOURCE
    // field, never interpret the value itself.
    const showProviderName = WBSResponse?.WBSData.showProviderName
    const accommodationsOptions: DestinationOption[] =
      WBSResponse?.WBSData.hotels.map((h) => ({
        destinationName: lang === 'fa' ? h.name.fa : h.name.en,
        destinationCode: h.propertyCode,
        cityOfAccommodation: lang === 'fa' ? h.cityName.fa : h.cityName.en,
        isCity: false,
        cityID: undefined,
        providerCode:
          (showProviderName ? h.defaultProviderCode : h.defaultRateGroupCode) ??
          '',
        bookingRedirect: h.bookingRedirect
      })) || []
    if (accommodationsOptions.length === 1) {
      setBookingRedirect(accommodationsOptions[0].bookingRedirect)
      const resolvedProviderCode = accommodationsOptions[0].providerCode
      setSearchAvailabilityInput((prev) => ({
        ...prev,
        propertyCode: accommodationsOptions[0].destinationCode,
        // Omit the filter entirely rather than sending providerCodes: [''] when neither
        // field resolved (the backend itself failed to resolve either) -- an empty-string
        // filter value would silently zero out search results instead of surfacing the
        // anomaly, and this case has nothing safe to filter by anyway.
        providerCodes: resolvedProviderCode
          ? [resolvedProviderCode]
          : undefined,
        cityID: undefined
      }))
    }

    setOptions([...accommodationsOptions])
  }, [WBSResponse])

  useEffect(() => {
    setSearchAvailabilityInput((prev) => ({
      ...prev,
      arrival: dateObject?.gregorianStartDate || '',
      departure: dateObject?.gregorianEndDate || '',
      guestsAges: Array(guestsNumber).fill(24)
    }))
  }, [dateObject, guestsNumber])

  const useEffectFunction = () => {
    function handleClickOutside(event: MouseEvent) {
      // eventOriginatedInside() rather than ref.contains(event.target): this component also
      // renders inside a shadow root (the embed), where events are RETARGETED to the shadow
      // host, making contains() report every inside click as outside. Behaviour is identical
      // in the light DOM. See ADR-034 H1 and helpers/__tests__/domEvents.test.ts.
      if (
        datePickerRef?.current &&
        !eventOriginatedInside(event, datePickerRef.current) &&
        selectRangeModalIsOpen
      ) {
        setSelectRangeModalIsOpen(false)
      }
      if (
        guestInfoRef.current &&
        !eventOriginatedInside(event, guestInfoRef.current) &&
        selectNumberOfGuestsModalIsOpen
      ) {
        setSelectNumberOfGuestsModalIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }
  const SearchWidgetDesktopInterfaceFunction = () => {
    return (
      <SearchWidgetDesktopInterface
        selectNumberOfGuestsModalIsOpen={selectNumberOfGuestsModalIsOpen}
        setSelectNumberOfGuestsModalIsOpen={setSelectNumberOfGuestsModalIsOpen}
        selectRangeModalIsOpen={selectRangeModalIsOpen}
        setSelectRangeModalIsOpen={setSelectRangeModalIsOpen}
        effectFunction={useEffectFunction}
        type={lang === 'fa' ? 'jalaali' : 'gregorian'}
        readMode={readMode}
        setreadMode={setreadMode}
        options={options}
        searchAvailabilityInput={searchAvailabilityInput}
        guestInfoRef={guestInfoRef}
        datePickerRef={datePickerRef}
        setguestsNumber={setguestsNumber}
        guestsNumber={guestsNumber}
        setdateObject={setdateObject}
        dateObject={dateObject}
        setSearchAvailabilityInput={setSearchAvailabilityInput}
        loadingForWBSData={loadingForWBSData}
        bookingRedirect={bookingRedirect}
        setBookingRedirect={setBookingRedirect}
        isFromBooking={isFromBooking}
        noShowDistination={
          SITE_KEY?.includes('single')
            ? SITE_KEY?.includes('single')
            : options.length === 1
        }
        selectingStartDate={selectingStartDate}
        setSelectingStartDate={setSelectingStartDate}
      />
    )
  }
  const SearchWidgetMobileInterfaceFunction = () => {
    return (
      <SearchWidgetMobileInterface
        selectRangeModalIsOpen={selectRangeModalIsOpen}
        setSelectRangeModalIsOpen={setSelectRangeModalIsOpen}
        selectNumberOfGuestsModalIsOpen={selectNumberOfGuestsModalIsOpen}
        setSelectNumberOfGuestsModalIsOpen={setSelectNumberOfGuestsModalIsOpen}
        dateObject={dateObject}
        setdateObject={setdateObject}
        guestsNumber={guestsNumber}
        setguestsNumber={setguestsNumber}
        datePickerRef={datePickerRef}
        guestInfoRef={guestInfoRef}
        effectFunction={useEffectFunction}
        type={lang === 'fa' ? 'jalaali' : 'gregorian'}
        searchAvailabilityInput={searchAvailabilityInput}
        options={options}
        setreadMode={setreadMode}
        readMode={readMode}
        loadingForWBSData={loadingForWBSData}
        bookingRedirect={bookingRedirect}
        setBookingRedirect={setBookingRedirect}
        setSearchAvailabilityInput={setSearchAvailabilityInput}
        isFromBooking={isFromBooking}
        selectingStartDate={selectingStartDate}
        setSelectingStartDate={setSelectingStartDate}
      />
    )
  }
  const SearchWidgetTabletInterfaceFunction = () => {
    return (
      <SearchWidgetTabletInterface
        selectRangeModalIsOpen={selectRangeModalIsOpen}
        setSelectRangeModalIsOpen={setSelectRangeModalIsOpen}
        selectNumberOfGuestsModalIsOpen={selectNumberOfGuestsModalIsOpen}
        setSelectNumberOfGuestsModalIsOpen={setSelectNumberOfGuestsModalIsOpen}
        dateObject={dateObject}
        setdateObject={setdateObject}
        guestsNumber={guestsNumber}
        setguestsNumber={setguestsNumber}
        datePickerRef={datePickerRef}
        guestInfoRef={guestInfoRef}
        effectFunction={useEffectFunction}
        type={lang === 'fa' ? 'jalaali' : 'gregorian'}
        searchAvailabilityInput={searchAvailabilityInput}
        options={options}
        setreadMode={setreadMode}
        readMode={readMode}
        loadingForWBSData={loadingForWBSData}
        bookingRedirect={bookingRedirect}
        setBookingRedirect={setBookingRedirect}
        setSearchAvailabilityInput={setSearchAvailabilityInput}
        isFromBooking={isFromBooking}
        selectingStartDate={selectingStartDate}
        setSelectingStartDate={setSelectingStartDate}
      />
    )
  }

  return (
    <>
      <Hidden smDown>{SearchWidgetDesktopInterfaceFunction()}</Hidden>
      <Hidden xsDown mdUp>
        {SearchWidgetTabletInterfaceFunction()}
      </Hidden>
      <Hidden smUp>{SearchWidgetMobileInterfaceFunction()}</Hidden>
    </>
  )
}
export default SearchWidgetMain
